import api from "@/services/api/apiClient";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronRight,
  Clipboard,
  Clock,
  Copy,
  History,
  Hotel,
  Plane,
  QrCode,
  ShieldCheck,
  Ship,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DemoNavigator } from "../../components/demo/DemoNavigator";
import { PageBanner } from "../../components/ui/PageBanner";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { i18nToast } from "../../utils/i18nToast";

type OperatorType = "cruise" | "airline" | "hotel";
type DataScope = "base" | "emergency" | "vaccinations";

interface CheckInQRData {
  token: string;
  qrDataUrl: string;
  accessUrl: string;
  expiresAt: string;
  operatorType: OperatorType;
  operatorName?: string;
  bookingRef?: string;
  dataScope: DataScope[];
}

interface CheckInHistoryItem {
  id: string;
  operator_type: OperatorType;
  operator_name?: string;
  booking_ref?: string;
  data_scope: DataScope[];
  expires_at: string;
  scanned_at?: string;
  created_at: string;
}

// Stored codes stay as they are; labels are resolved through i18n at render.
const OPERATOR_CONFIG: Record<
  OperatorType,
  {
    labelKey: keyof Translation;
    icon: React.ElementType;
    color: string;
    bg: string;
  }
> = {
  cruise: {
    labelKey: "checkinOperatorCruise",
    icon: Ship,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  airline: {
    labelKey: "checkinOperatorAirline",
    icon: Plane,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  hotel: {
    labelKey: "checkinOperatorHotel",
    icon: Hotel,
    color: "text-green-600",
    bg: "bg-green-50",
  },
};

const DATA_SCOPE_KEYS: Record<
  DataScope,
  { label: keyof Translation; description: keyof Translation }
> = {
  base: { label: "checkinScopeBase", description: "checkinScopeBaseDesc" },
  emergency: {
    label: "checkinScopeEmergency",
    description: "checkinScopeEmergencyDesc",
  },
  vaccinations: {
    label: "checkinScopeVaccinations",
    description: "checkinScopeVaccinationsDesc",
  },
};

const EXPIRY_OPTIONS: { value: number; labelKey: keyof Translation }[] = [
  { value: 24, labelKey: "checkinExpiry24h" },
  { value: 72, labelKey: "checkinExpiry72h" },
  { value: 168, labelKey: "checkinExpiry7d" },
];

const CheckInPage: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const navigate = useNavigate();
  const label = (key: keyof Translation) => String(t[key] ?? key);
  const [step, setStep] = useState<"select" | "config" | "qr" | "confirmed">(
    "select",
  );
  const [operatorType, setOperatorType] = useState<OperatorType>("airline");
  const [operatorName, setOperatorName] = useState("");
  const [bookingRef, setBookingRef] = useState("");
  const [dataScope, setDataScope] = useState<DataScope[]>([
    "base",
    "emergency",
  ]);
  const [expiryHours, setExpiryHours] = useState(72);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState<CheckInQRData | null>(null);
  const [history, setHistory] = useState<CheckInHistoryItem[]>([]);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    if (!qrData) return;
    const updateTimer = () => {
      const diff = new Date(qrData.expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft(t.checkinExpired);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(
        t.checkinTimeLeft.replace("{h}", String(h)).replace("{m}", String(m)),
      );
    };
    updateTimer();
    const interval = setInterval(updateTimer, 30000);
    return () => clearInterval(interval);
  }, [qrData, t]);

  const loadHistory = async () => {
    try {
      const res = await api.get("/api/checkin/history");
      setHistory(res.data?.data ?? []);
    } catch {
      /* no-op */
    }
  };

  const toggleScope = (scope: DataScope) =>
    setDataScope((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );

  const generateQR = async () => {
    if (!dataScope.length) {
      i18nToast.error(t.checkinSelectScope);
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/api/checkin/generate", {
        operatorType,
        operatorName: operatorName || undefined,
        bookingRef: bookingRef || undefined,
        dataScope,
        expiryHours,
      });
      setQrData(res.data.data);
      setStep("qr");
      i18nToast.success(t.checkinQrGenerated);
      loadHistory();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? t.checkinQrFailed;
      i18nToast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async () => {
    if (!qrData?.accessUrl) return;
    try {
      await navigator.clipboard.writeText(qrData.accessUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const OConfig = OPERATOR_CONFIG[operatorType];
  const OperatorIcon = OConfig.icon;
  const operatorLabel = label(OConfig.labelKey);

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <PageBanner
        icon={Clipboard}
        title={t.checkinTitle}
        description={t.checkinDescription}
        gradient="from-green-600 to-teal-700"
      />

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <nav className="-mb-px flex space-x-5 overflow-x-auto">
          <button
            onClick={() => navigate("/traveler/health")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.edit}
          </button>
          <button
            onClick={() => navigate("/traveler/health/emergency")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabEmergency}
          </button>
          <button
            onClick={() => navigate("/traveler/health/appointment")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabAppointments}
          </button>
          <button className="border-b-2 border-green-500 py-3 px-1 text-sm font-medium text-green-600 whitespace-nowrap">
            {t.apptTabCheckin}
          </button>
          <button
            onClick={() => navigate("/traveler/health/insurance-request")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabInsurance}
          </button>
        </nav>
      </div>

      {/* DSGVO info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-800">
          <strong>{t.checkinPrivacyTitle}</strong> {t.checkinPrivacyText}
        </p>
      </div>

      {/* STEP 1 */}
      {step === "select" && (
        <div className="space-y-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              {t.checkinChooseOperator}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {(
                Object.entries(OPERATOR_CONFIG) as [
                  OperatorType,
                  (typeof OPERATOR_CONFIG)[OperatorType],
                ][]
              ).map(([type, cfg]) => {
                const Icon = cfg.icon;
                const sel = operatorType === type;
                return (
                  <button
                    key={type}
                    onClick={() => setOperatorType(type)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${sel ? `border-current ${cfg.color} ${cfg.bg}` : "border-gray-200 hover:border-gray-300 text-gray-600"}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">
                      {label(cfg.labelKey)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={() => setStep("config")}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
          >
            {t.checkinContinue} <ChevronRight className="w-4 h-4" />
          </button>
          {history.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-gray-600" />
                {t.checkinHistory}
              </h3>
              <div className="space-y-2">
                {history.slice(0, 5).map((item) => {
                  const c = OPERATOR_CONFIG[item.operator_type];
                  const Icon = c?.icon ?? Plane;
                  const expired = new Date(item.expires_at) < new Date();
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0"
                    >
                      <div className={`p-1.5 rounded ${c?.bg ?? "bg-gray-50"}`}>
                        <Icon
                          className={`w-4 h-4 ${c?.color ?? "text-gray-600"}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.operator_name ??
                            (c ? label(c.labelKey) : item.operator_type)}
                          {item.booking_ref ? ` · ${item.booking_ref}` : ""}
                        </p>
                        <p className="text-xs text-gray-500">
                          {fmt.date(item.created_at)}
                          {item.scanned_at ? ` · ${t.checkinScanned}` : ""}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${expired ? "bg-gray-100 text-gray-500" : "bg-green-100 text-green-700"}`}
                      >
                        {expired ? t.checkinExpired : t.checkinActive}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 2 */}
      {step === "config" && (
        <div className="space-y-5">
          <button
            onClick={() => setStep("select")}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← {t.back}
          </button>
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <OperatorIcon className={`w-5 h-5 ${OConfig.color}`} />
              {t.checkinDetails.replace("{operator}", operatorLabel)}
            </h3>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                {t.checkinOperatorName}{" "}
                <span className="text-gray-400 font-normal">
                  {t.apptOptionalHint}
                </span>
              </label>
              <input
                type="text"
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                placeholder={
                  operatorType === "airline"
                    ? "Thai Airways"
                    : operatorType === "cruise"
                      ? "HARMONY OF THE SEAS"
                      : t.checkinHotelPlaceholder
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                {t.checkinBookingRef}{" "}
                <span className="text-gray-400 font-normal">
                  {t.apptOptionalHint}
                </span>
              </label>
              <input
                type="text"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                placeholder="TG-2026-BKK-001"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3">
              {t.checkinSharedData}
            </h3>
            <div className="space-y-2">
              {(
                Object.entries(DATA_SCOPE_KEYS) as [
                  DataScope,
                  (typeof DATA_SCOPE_KEYS)[DataScope],
                ][]
              ).map(([scope, meta]) => (
                <label
                  key={scope}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={dataScope.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    className="w-4 h-4 rounded text-green-600"
                  />
                  <div>
                    <span className="font-medium text-sm text-gray-900">
                      {label(meta.label)}
                    </span>
                    <p className="text-xs text-gray-500">
                      {label(meta.description)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-600" />
              {t.checkinValidity}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {EXPIRY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setExpiryHours(opt.value)}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${expiryHours === opt.value ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                >
                  {label(opt.labelKey)}
                </button>
              ))}
            </div>
          </div>
          {dataScope.length === 0 && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {t.checkinSelectScope}
            </div>
          )}
          <button
            onClick={generateQR}
            disabled={loading || dataScope.length === 0}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.checkinGenerating}
              </>
            ) : (
              <>
                <QrCode className="w-5 h-5" />
                {t.checkinGenerate}
              </>
            )}
          </button>
        </div>
      )}

      {/* STEP 3 */}
      {step === "qr" && qrData && (
        <div className="space-y-5">
          <button
            onClick={() => setStep("config")}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← {t.back}
          </button>
          <div className="bg-white border-2 border-green-200 rounded-2xl p-6 text-center shadow-md">
            <div className="flex justify-center mb-2">
              <OperatorIcon className={`w-7 h-7 ${OConfig.color}`} />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1">
              {t.checkinQrHeading.replace("{operator}", operatorLabel)}
            </h3>
            {qrData.operatorName && (
              <p className="text-sm text-gray-600 mb-1">
                {qrData.operatorName}
              </p>
            )}
            {qrData.bookingRef && (
              <p className="text-xs text-gray-400 mb-3">
                {t.checkinBookingRefShort.replace("{ref}", qrData.bookingRef)}
              </p>
            )}
            <div className="flex justify-center mb-4">
              <img
                src={qrData.qrDataUrl}
                alt={t.checkinQrAlt}
                className="w-52 h-52 rounded-lg shadow-sm"
              />
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-orange-600 bg-orange-50 rounded-lg py-2 px-4 mb-3">
              <Clock className="w-4 h-4" />
              {timeLeft}
            </div>
            <div className="flex flex-wrap gap-1.5 justify-center mb-4">
              {qrData.dataScope.map((s) => (
                <span
                  key={s}
                  className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium"
                >
                  ✓ {DATA_SCOPE_KEYS[s] ? label(DATA_SCOPE_KEYS[s].label) : s}
                </span>
              ))}
            </div>
            <button
              onClick={copyUrl}
              className="flex items-center justify-center gap-2 w-full py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {copiedUrl ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copiedUrl ? t.checkinCopied : t.checkinCopyLink}
            </button>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800">{t.checkinStaffScans}</p>
          </div>
          <button
            onClick={() => {
              setStep("confirmed");
              i18nToast.success(t.checkinCompleted);
            }}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
          >
            {t.checkinConfirm}
          </button>
        </div>
      )}

      {/* STEP 4 */}
      {step === "confirmed" && (
        <div className="text-center space-y-5 py-6">
          <div className="flex justify-center">
            <div className="p-5 bg-green-100 rounded-full">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t.checkinSuccess}
          </h2>
          <p className="text-gray-600 max-w-md mx-auto">
            {(() => {
              const [before, after] = t.checkinSharedWith.split("{operator}");
              return (
                <>
                  {before}
                  <strong>{qrData?.operatorName ?? operatorLabel}</strong>
                  {after}
                </>
              );
            })()}
            {qrData && (
              <>
                {" "}
                {t.checkinAccessExpires.replace(
                  "{date}",
                  fmt.date(qrData.expiresAt),
                )}
              </>
            )}
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <button
              onClick={() => {
                setStep("select");
                setQrData(null);
                setOperatorName("");
                setBookingRef("");
              }}
              className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t.checkinNew}
            </button>
            <button
              onClick={() => navigate("/traveler/health/appointment")}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"
            >
              {t.checkinToAppointments}
            </button>
          </div>
        </div>
      )}

      <DemoNavigator currentStep={3} />
    </div>
  );
};

export default CheckInPage;
